import React, { createRef, PureComponent } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import debounce from 'lodash/debounce';
import IndicatorsOptions from './IndicatorsOptions';
import { isVisibleIndicator } from '../../../../ReactSharedScripts/modules/detailPage/components/KeyIndicators/utils';
import classes from './styles/index.module.scss';
import { AngleLeftSmall } from '../../../../ReactSharedScripts/components/Icons/AngleLeftSmall';
import { IconButton } from '../../../../ReactSharedScripts/components/IconButton';
import { AngleRightSmall } from '../../../../ReactSharedScripts/components/Icons/AngleRightSmall';

export class KeyIndicators extends PureComponent {
	static ANIMATION_DURATION = 700;

	static SCROLL_VALUE = 320;

	syncState = {
		onWindowResize: null,
		refs: {
			container: createRef(),
		},
	};

	state = {
		hasScroll: false,
	};

	constructor(props) {
		super(props);

		this.syncState.onWindowResize = debounce(this.handleWindowResize, 300);
	}

	componentDidMount() {
		const { current } = this.syncState.refs.container;
		const scrollHeight = current.offsetHeight - current.clientHeight;
		const scrollWidth = current.offsetWidth - current.clientWidth;
		if (scrollWidth > 0) {
			current.style.paddingRight = `${scrollWidth}px`;
		}

		if (scrollHeight > 0) {
			current.style.paddingBottom = `${scrollHeight}px`;
		}

		this.setHasScroll();

		window.addEventListener('resize', this.syncState.onWindowResize);
	}

	componentDidUpdate(prevProps) {
		if (prevProps.items.length !== this.props.items.length) {
			this.setHasScroll();
		}
	}

	componentWillUnmount() {
		window.removeEventListener('resize', this.syncState.onWindowResize);
	}

	setHasScroll = () => {
		this.setState({ hasScroll: this.isInnerScrollExist });
	};

	get isScrollStart() {
		const { current } = this.syncState.refs.container;

		if (current === null) {
			return false;
		}

		return current.scrollLeft === 0;
	}

	get isScrollEnd() {
		const { current } = this.syncState.refs.container;

		if (current === null) {
			return false;
		}

		const $innerContainer = current;
		const maxScrollLeft = $innerContainer.scrollWidth - $innerContainer.clientWidth;

		return $innerContainer.scrollLeft === maxScrollLeft;
	}

	get isInnerScrollExist() {
		const { current } = this.syncState.refs.container;

		return current !== null && current.scrollWidth > current.clientWidth;
	}

	handleWindowResize = () => {
		this.setHasScroll();
	};

	handleScroll = (value) => {
		const $innerContainer = this.syncState.refs.container.current;
		const currentScrollValue = $innerContainer.scrollLeft;
		this.animate({
			duration: KeyIndicators.ANIMATION_DURATION,
			timing: (t) => (t -= 1) * t * t + 1,
			draw: (progress) => {
				//somehow value gets negative on the first tick of requestAnimationFrame
				if (progress >= 0) {
					$innerContainer.scrollLeft = currentScrollValue + value * progress;
				}
			},
		});
	};

	handleBackScroll = () => {
		this.handleScroll(-Math.abs(KeyIndicators.SCROLL_VALUE));
	};

	handleForwardScroll = () => {
		this.handleScroll(Math.abs(KeyIndicators.SCROLL_VALUE));
	};

	handleNativeScroll = () => {
		this.forceUpdate();
	};

	animate = (options) => {
		const start = performance.now();

		requestAnimationFrame(function animate(time) {
			// timeFraction [0, 1]
			let timeFraction = (time - start) / options.duration;
			if (timeFraction > 1) timeFraction = 1;

			const progress = options.timing(timeFraction);

			options.draw(progress);

			if (timeFraction < 1) {
				requestAnimationFrame(animate);
			}
		});
	};

	render() {
		const { hasScroll } = this.state;
		const leftArrowClassNames = classNames(classes.control, classes.control_round, classes.control_left, {
			[classes.control_disabled]: this.isScrollStart,
		});

		const rightArrowClassNames = classNames(classes.control, classes.control_round, classes.control_right, {
			[classes.control_disabled]: this.isScrollEnd,
		});

		const { items, hasIndicatorsEdit, selectedItems, tempSelectedItems, onTempChange, onChange, disabled } =
			this.props;
		const componentClasses = classNames(classes.indicators, this.props.className);

		const showNavigationButtons = hasScroll;
		return (
			<div
				className={componentClasses}
				data-testid='keyIndicators'
			>
				{showNavigationButtons && (
					<IconButton
						className={leftArrowClassNames}
						onClick={this.handleBackScroll}
					>
						<AngleLeftSmall />
					</IconButton>
				)}
				<div className={classes.indicators__container}>
					<div
						className={classNames(classes.indicators__inner, this.props.indicatorsInnerClassName)}
						ref={this.syncState.refs.container}
						onScroll={debounce(this.handleNativeScroll)}
					>
						{items.map((indicator) => {
							const isVisible = !hasIndicatorsEdit || isVisibleIndicator(indicator, selectedItems);

							return (
								isVisible && (
									<div
										key={indicator.id}
										className={classes.indicator}
										data-testid={'keyIndicators__indicator'}
									>
										{this.props.render(indicator)}
									</div>
								)
							);
						})}
					</div>
				</div>
				{hasIndicatorsEdit && (
					<IndicatorsOptions
						indicators={items}
						selectedItems={selectedItems}
						tempSelectedItems={tempSelectedItems}
						onTempChange={onTempChange}
						onChange={onChange}
						disabled={disabled}
					/>
				)}
				{showNavigationButtons && (
					<IconButton
						className={rightArrowClassNames}
						onClick={this.handleForwardScroll}
					>
						<AngleRightSmall />
					</IconButton>
				)}
			</div>
		);
	}
}

KeyIndicators.propTypes = {
	items: PropTypes.array,
	selectedItems: PropTypes.array,
	hasIndicatorsEdit: PropTypes.bool,
	tempSelectedItems: PropTypes.array,
	onChange: PropTypes.func,
	onTempChange: PropTypes.func,
	indicatorsInnerClassName: PropTypes.string,
};

KeyIndicators.defaultProps = {
	items: [],
	selectedItems: [],
	tempSelectedItems: [],
	hasIndicatorsEdit: true,
	onChange: () => {},
};

export default KeyIndicators;
